import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import Icon from '@/Components/UI/Icon/Icon';
import InputField from '@/Components/UI/Form/InputField/InputField';
import { getTypeIconPath } from '@/Utils/getTypeIconPath';

type Option = {
  label: string;
  value: string;
  type?: string;
};

type Props = {
  label?: string;
  value: string;
  options: Option[];
  onChange: (event: string) => void;
  size?: 'small' | 'large';
  color?: 'shadow' | 'oceanBlue' | 'violetWeb';
  disabled?: boolean;
  helperText?: string;
  helperTextPosition?: 'top' | 'bottom';
  dataCy?: string;
  buttonClassName?: string;
};

const selectColors = {
  shadow: 'border-shadow-200 text-ui-secondary !shadow-none placeholder-helper font-normal',
  oceanBlueLight: 'border-shadow-200 text-ui-secondary !shadow-none placeholder-helper font-normal',
  oceanBlue: 'border-ocean-blue-500 shadow-ocean-blue-100 text-ui placeholder-ui font-normal',
  violetWeb: 'border-violet-web-700 shadow-violet-web-100 text-ui placeholder-ui font-normal',
};

const activeColors = {
  shadow: 'bg-shadow text-ui',
  oceanBlue: 'bg-ocean-blue-500 text-ui',
  oceanBlueLight: 'bg-ocean-blue-100 text-ui rounded',
  violetWeb: 'bg-violet-web-700 text-ui',
};

const accessibilityColors = 'focus:outline-none';

const QuickFiltersSelect = ({
  options,
  value,
  label,
  helperText,
  helperTextPosition = 'bottom',
  size = 'large',
  color = 'shadow',
  disabled,
  dataCy,
  onChange,
  buttonClassName,
}: Props) => {
  const [defaultOption, setDefaultOption] = useState<Option | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [positioning, setPositioning] = useState({ top: 0, left: 0, height: 0 });

  useEffect(() => {
    const rect = buttonRef?.current?.getBoundingClientRect();
    if (rect) {
      const { top, left, height } = rect;
      setPositioning({
        top,
        left,
        height,
      });
    }
  }, []);

  useEffect(() => {
    const defaultOption = options.find((option) => option.value === value);
    setDefaultOption(defaultOption);
  }, [value, options]);

  const handleOptionSelect = (selectedValue: string) => {
    onChange(selectedValue);
  };

  return (
    <>
      <div className='flex flex-row items-center gap-1 pt-1 pb-2 text-sm font-semibold'>
        <Icon name='filter' size={15} color='#151515' />
        Filter By
      </div>
      <InputField
        dataCy={dataCy}
        label={label}
        helperText={helperText}
        helperTextPosition={helperTextPosition}
        color={color}
        disabled={disabled}
        className='w-full'
      >
        <div className='relative'>
          <Listbox value={value} onChange={handleOptionSelect}>
            {({ open }) => (
              <>
                <Listbox.Button
                  ref={buttonRef}
                  className={clsx(
                    'w-full border appearance-none flex items-center bg-white px-3 rounded disabled:bg-ui-secondary !disabled:text-helper disabled:border-shadow-200 relative text-xs',
                    size === 'large'
                      ? 'shadow-spread h-8 text-sm'
                      : 'h-6 text-[13px] leading-[18px]',
                    color && selectColors[color],
                    accessibilityColors,
                    buttonClassName
                  )}
                  data-cy={dataCy}
                >
                  {defaultOption && defaultOption.type && (
                    <Icon
                      name={getTypeIconPath(defaultOption.type)}
                      className='flex-shrink-0 mr-1'
                    />
                  )}
                  {defaultOption && defaultOption.label}
                  <Icon
                    className='absolute ml-2 right-2'
                    name={open ? 'caret-up' : 'caret-down-regular'}
                    size={12}
                    color='#151515'
                  />
                </Listbox.Button>

                {open &&
                  ReactDOM.createPortal(
                    <Listbox.Options
                      unmount
                      ref={optionsRef}
                      className={clsx(
                        'scrollbar-thin w-full border bg-white border-ui-100 rounded absolute z-50 overflow-auto p-1 h-[21vh] portal-select',

                        size === 'large' ? 'text-sm' : 'text-xs',
                        color && selectColors[color],
                        accessibilityColors
                      )}
                      style={{
                        top: positioning.top + positioning.height + 8,

                        left: positioning.left,
                        width: buttonRef.current?.offsetWidth,
                      }}
                    >
                      {options.map((option, i) => (
                        <Listbox.Option
                          data-rec='qf-SelectOption'
                          data-cy={`select-option-${i + 1}`}
                          id={option.label + i}
                          className={({ active }: { active: boolean }) =>
                            clsx(
                              'relative leading-5 text-ui flex items-center cursor-pointer select-none py-2 pl-3 pr-9 whitespace-pre overflow-hidden portal-select',
                              size === 'large' ? 'h-8 text-sm' : 'h-6 text-[13px]',
                              color && selectColors[color],
                              accessibilityColors,
                              active ? activeColors[color] : 'text-ui'
                            )
                          }
                          key={option.label + i}
                          value={option.value}
                          onClick={() => {
                            handleOptionSelect(option.value);
                          }}
                        >
                          {option.type && (
                            <Icon
                              name={getTypeIconPath(option.type)}
                              className='flex-shrink-0 mr-1'
                            />
                          )}
                          {option.label}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>,
                    document.body
                  )}
              </>
            )}
          </Listbox>
        </div>
      </InputField>
    </>
  );
};
export default QuickFiltersSelect;
